import * as React from 'react';
import dynamic from 'next/dynamic';

import {
  useGridSelector,
  useGridApiContext,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridCellModes
} from '@mui/x-data-grid-pro';

const GridToolbarContainer = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarContainer), { ssr: false }
);

const GridToolbarExportContainer = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarExportContainer), { ssr: false }
);

const GridToolbarColumnsButton = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarColumnsButton), { ssr: false }
);

const GridToolbarFilterButton = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarFilterButton), { ssr: false }
);

const GridPrintExportMenuItem = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridPrintExportMenuItem), { ssr: false }
);

const GridToolbarDensitySelector = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarDensitySelector), { ssr: false }
);

const GridToolbarQuickFilter = dynamic(() =>
  import('@mui/x-data-grid-pro').then(mod => mod.GridToolbarQuickFilter), { ssr: false }
);



import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandCircleDownRounded';
import { styled } from '@mui/material/styles';
import JobContent from '../Components/Account/Jobs/JobContent';
import RecordContainer from '../Components/Dashboard/TableRows/RecordContainer';
import { useState, useCallback } from 'react';
import { count } from '../config/helpers';
import useAuth from './useAuth';
import useWindowSize from './useWindowSize';
import MenuItem from '@mui/material/MenuItem';

//OVERLAY
const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
    fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
  },
}));



//default date range of yesterday/today
const useDataGrid = (defaultPagination = 25) => {


  const [panelExpanded, setPanelExpanded] = useState([]);
  const [cellModesModel, setCellModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: defaultPagination,
    page: 0,
  });
  const { isAgent } = useAuth();
  const { isMobile } = useWindowSize();

  const handlePanelChange = useCallback((ids) => {
    //this function only adds to the list of selected rows

    //so we set an empty array
    let clicked_row_id = [];

    //check if an id is selected (empty array or has index)
    if (count(ids) != 0) {

      //if it does we grab the last selected row id
      clicked_row_id = [ids[ids.length - 1]]
    }

    //set the expanded to only this index
    setPanelExpanded(clicked_row_id)
  })


  function DetailPanelContent(props) {
    const {
      type,
      row,
      customer_id
    } = props;

    if (type == 'upcoming_jobs') {
      return (
        <JobContent job={row} />
      )
    } else {
      return (
        <div className="w-100 margin-auto vertical-auto">
          <RecordContainer customer_id={customer_id} row={row} />
        </div>
      )
    }
  }

  const handleCellClick = useCallback((params) => {
    if (!params.isEditable) return;

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View }
              }),
              {}
            )
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit }
        }
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const selectedCount = React.useMemo(() => {
    if (!selectedRows) return 0;
    if (Array.isArray(selectedRows)) return selectedRows.length;
    if (selectedRows instanceof Set) return selectedRows.size;
    if (selectedRows?.ids instanceof Set) return selectedRows.ids.size;
    if (Array.isArray(selectedRows?.ids)) return selectedRows.ids.length;
    return 0;
  }, [selectedRows]);

  //function to disable filters and menus which make it difficult to read on mobile
  const disableNotAgentOrMobile = () => {
    const condition = !isAgent() || isMobile;
    return {
      disableColumnFilter: condition,
      disableColumnMenu: condition,
      disableColumnSelector: condition,
    }

  }

  //hide search toolbar and export options on mobile
  const showToolBar = () => {
    return !isMobile;
  }

  const handlePagination = (newModel) => {
    setPaginationModel(newModel);
  }


  return {
    panelExpanded, setPanelExpanded,
    handlePanelChange,
    DetailPanelContent,
    singleCellModel: {
      cellModesModel,
      handleCellClick,
      handleCellModesModelChange,
    },
    Toolbar: {
      disableNotAgentOrMobile,
      showToolBar
    },
    checkedRows: {
      setSelectedRows,
      selectedRows,
      selectedCount,
      handleSelectionChange
    },
    pagination: {
      paginationModel,
      setPaginationModel,
      handlePagination
    },
  }
}

export default useDataGrid;